import {
	fetchAuthSession,
	type AuthSession,
	type FetchAuthSessionOptions,
} from 'aws-amplify/auth';

const DEFAULT_MAX_RETRIES = 3;
const INITIAL_DELAY_MS = 500;

/** True when Amplify returned a Cognito ID token (required for protected routes). */
export function hasCognitoIdToken(
	session: AuthSession | null | undefined
): boolean {
	return Boolean(session?.tokens?.idToken);
}

export function isUnauthenticatedError(error: unknown): boolean {
	if (!error || typeof error !== 'object') {
		return false;
	}
	const name = (error as { name?: string }).name ?? '';
	const message = String(
		(error as { message?: string }).message ?? ''
	).toLowerCase();
	return (
		name === 'NotAuthenticatedError' ||
		name === 'UserUnAuthenticatedException' ||
		name === 'NoSessionFoundException' ||
		message.includes('no current user') ||
		message.includes('user is empty') ||
		message.includes('user is not authenticated') ||
		message.includes('no access token') ||
		message.includes('novalidauthtokens') ||
		message.includes('no federated jwt')
	);
}

export function isTransientAuthError(error: unknown): boolean {
	if (isUnauthenticatedError(error)) {
		return false;
	}
	if (!error) {
		return false;
	}
	const message = String(
		(error as { message?: string }).message ?? ''
	).toLowerCase();
	const name = String((error as { name?: string }).name ?? '');

	if (name === 'NetworkError') {
		return true;
	}
	if (
		message.includes('504') ||
		message.includes('503') ||
		message.includes('502') ||
		message.includes('429')
	) {
		return true;
	}
	if (
		message.includes('gateway') ||
		message.includes('timeout') ||
		message.includes('network error')
	) {
		return true;
	}
	if (message.includes("failed to execute 'json' on 'response'")) {
		return true;
	}
	if (message.includes('unexpected end of json input')) {
		return true;
	}
	if (message.includes('empty response body')) {
		return true;
	}
	const statusMatch = message.match(/status (\d{3})/);
	if (statusMatch) {
		const status = Number.parseInt(statusMatch[1], 10);
		return status >= 500 || status === 429;
	}
	return false;
}

function sleep(ms: number): Promise<void> {
	return new Promise(resolve => {
		setTimeout(resolve, ms);
	});
}

/**
 * fetchAuthSession with exponential backoff for transient Cognito/gateway failures.
 */
export async function fetchAuthSessionWithRetry(
	options?: FetchAuthSessionOptions,
	maxRetries = DEFAULT_MAX_RETRIES
): Promise<AuthSession> {
	let lastError: unknown;
	for (let attempt = 0; attempt <= maxRetries; attempt++) {
		try {
			return await fetchAuthSession(options);
		} catch (error) {
			lastError = error;
			if (!isTransientAuthError(error) || attempt === maxRetries) {
				throw error;
			}
			await sleep(INITIAL_DELAY_MS * 2 ** attempt);
		}
	}
	throw lastError;
}

/**
 * Graceful wrapper: retries transient failures, returns null when auth cannot be resolved.
 */
export async function safeFetchAuthSession(
	options?: FetchAuthSessionOptions
): Promise<AuthSession | null> {
	try {
		return await fetchAuthSessionWithRetry(options);
	} catch (error) {
		if (!isUnauthenticatedError(error)) {
			console.error('fetchAuthSession failed:', error);
		}
		return null;
	}
}
